// Main modules export file for Profound Philosophy
// Each content module carries its own metadata (title, level, duration, category).
// The wrappers below normalise the fields the app relies on: string ids (used in
// routes and progress keys), difficulty labels for the lock logic, ordering, and
// prerequisite chains mapped from the content files' numeric module references.
// `emoji` is the card tile stand-in until module hero artwork exists; the content
// files' own `icon` is a lucide component and cannot be used in innerHTML fallbacks.
import module01WhatIsPhilosophy from './modules/philosophy-module-01-what-is-philosophy';
import module02LogicAndReasoning from './modules/philosophy-module-02-logic-and-reasoning';
import module03Epistemology from './modules/philosophy-module-03-epistemology';
import module04EthicsMoralPhilosophy from './modules/philosophy-module-04-ethics-moral-philosophy';
import module05MetaphysicsReality from './modules/philosophy-module-05-metaphysics-reality';
import module06PhilosophyOfMind from './modules/philosophy-module-06-philosophy-of-mind';
import module07SocialPoliticalPhilosophy from './modules/philosophy-module-07-social-political-philosophy';
import module08ExistentialismMeaning from './modules/philosophy-module-08-existentialism-meaning';

// Module metadata and organisation - Profound Philosophy Curriculum
export const modulesByLevel = {
  foundation: [
    {
      ...module01WhatIsPhilosophy,
      id: 'what-is-philosophy',
      emoji: '🦉',
      difficulty: 'Foundation',
      order: 1,
      prerequisites: []
    },
    {
      ...module02LogicAndReasoning,
      id: 'logic-and-reasoning',
      emoji: '🧭',
      difficulty: 'Foundation',
      order: 2,
      prerequisites: ['what-is-philosophy']
    },
    {
      ...module03Epistemology,
      id: 'epistemology',
      emoji: '🔍',
      difficulty: 'Foundation',
      order: 3,
      prerequisites: ['what-is-philosophy', 'logic-and-reasoning']
    },
    {
      ...module04EthicsMoralPhilosophy,
      id: 'ethics-moral-philosophy',
      emoji: '⚖️',
      difficulty: 'Foundation',
      order: 4,
      prerequisites: ['what-is-philosophy', 'logic-and-reasoning', 'epistemology']
    },
    {
      ...module05MetaphysicsReality,
      id: 'metaphysics-reality',
      emoji: '🌌',
      difficulty: 'Foundation',
      order: 5,
      prerequisites: ['what-is-philosophy', 'logic-and-reasoning', 'epistemology']
    }
  ],
  essential: [],
  advanced: [
    {
      ...module06PhilosophyOfMind,
      id: 'philosophy-of-mind',
      emoji: '🧠',
      difficulty: 'Advanced',
      order: 6,
      prerequisites: ['what-is-philosophy', 'metaphysics-reality']
    },
    {
      ...module07SocialPoliticalPhilosophy,
      id: 'social-political-philosophy',
      emoji: '🏛️',
      difficulty: 'Advanced',
      order: 7,
      prerequisites: ['what-is-philosophy', 'ethics-moral-philosophy']
    },
    {
      ...module08ExistentialismMeaning,
      id: 'existentialism-meaning',
      emoji: '🗻',
      difficulty: 'Advanced',
      order: 8,
      prerequisites: ['what-is-philosophy', 'logic-and-reasoning']
    }
  ]
};

// Get all modules as flat array
export const getAllModules = () => {
  return Object.values(modulesByLevel).flat();
};

// Get modules by difficulty level
export const getModulesByDifficulty = (difficulty) => {
  return modulesByLevel[difficulty.toLowerCase()] || [];
};

// Get available modules based on completed modules
export const getAvailableModules = (completedModuleIds = []) => {
  const allModules = getAllModules();

  return allModules.filter(module => {
    // Foundation modules are always available
    if (module.difficulty === 'Foundation') return true;

    // Check if all prerequisites are completed
    if (!module.prerequisites || module.prerequisites.length === 0) return true;

    return module.prerequisites.every(prereqId =>
      completedModuleIds.includes(prereqId)
    );
  });
};

// Get next recommended module based on progress
export const getNextRecommendedModule = (completedModuleIds = []) => {
  const availableModules = getAvailableModules(completedModuleIds);
  const incompleteModules = availableModules.filter(
    module => !completedModuleIds.includes(module.id)
  );

  // Return the lowest order incomplete module
  return incompleteModules.sort((a, b) => a.order - b.order)[0] || null;
};

// Get module by ID
export const getModuleById = (moduleId) => {
  return getAllModules().find(module => module.id === moduleId);
};

// Get module progress percentage
export const getModuleProgress = (moduleId, userProgress = {}) => {
  const moduleData = userProgress[moduleId];
  if (!moduleData) return 0;

  return moduleData.progressPercentage || 0;
};

// Check if module is locked
export const isModuleLocked = (moduleId, completedModuleIds = []) => {
  const module = getModuleById(moduleId);
  if (!module) return true;

  // Foundation modules are never locked
  if (module.difficulty === 'Foundation') return false;

  // Check prerequisites
  if (!module.prerequisites || module.prerequisites.length === 0) return false;

  return !module.prerequisites.every(prereqId =>
    completedModuleIds.includes(prereqId)
  );
};

// Get module statistics
export const getModuleStats = () => {
  const allModules = getAllModules();
  return {
    total: allModules.length,
    foundation: modulesByLevel.foundation.length,
    essential: modulesByLevel.essential.length,
    advanced: modulesByLevel.advanced.length
  };
};

export default {
  modulesByLevel,
  getAllModules,
  getModulesByDifficulty,
  getAvailableModules,
  getNextRecommendedModule,
  getModuleById,
  getModuleProgress,
  isModuleLocked,
  getModuleStats
};
